import React, { useState, useEffect, createContext, useContext } from "react";
import { authAPI } from "./services/api";
import Dashboard from "./components/Dashboard";
import JobsManager from "./components/JobsManager";
import MasterFiles from "./components/MasterFiles";
import Users from "./components/Users";
import ActivityLogs from "./components/ActivityLogs";
import "./App.css";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem("gelato_token");
      const savedUser = localStorage.getItem("gelato_user");

      if (token && savedUser) {
        try {
          // Verify token with backend
          const response = await authAPI.getProfile();
          if (response.data.success) {
            setUser(response.data.user);
            setIsAuthenticated(true);
          } else {
            throw new Error("Invalid token");
          }
        } catch (verifyError) {
          // Token verification failed, use saved data as fallback for demo
          console.warn(
            "Token verification failed, using saved data:",
            verifyError.message
          );
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setIsAuthenticated(true);
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      // Clear invalid tokens
      localStorage.removeItem("gelato_token");
      localStorage.removeItem("gelato_refresh_token");
      localStorage.removeItem("gelato_user");
      // Remove master data localStorage (deprecated)
      localStorage.removeItem("gelato_master_data");
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      // Use actual API call instead of demo mode
      const response = await authAPI.login(credentials);

      if (response.data.success) {
        const { user: userData, accessToken } = response.data.data;

        localStorage.setItem("gelato_token", accessToken);
        localStorage.setItem("gelato_user", JSON.stringify(userData));

        setUser(userData);
        setIsAuthenticated(true);

        return { success: true };
      } else {
        return {
          success: false,
          message: response.data.message || "เข้าสู่ระบบไม่สำเร็จ",
        };
      }
    } catch (error) {
      console.error("Login failed:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "เกิดข้อผิดพลาดในการเข้าสู่ระบบ",
      };
    }
  };

  const logout = async () => {
    try {
      // Uncomment for actual API call
      // await authAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("gelato_token");
      localStorage.removeItem("gelato_refresh_token");
      localStorage.removeItem("gelato_user");
      // Clean up any legacy localStorage data
      localStorage.removeItem("gelato_master_data");
      localStorage.removeItem("gelato_comparison_results");
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === "ผู้ดูแลระบบ") return true;
    return user.permissions?.[permission] | false;
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("gelato_user", JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        hasPermission,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const LoginForm = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.username || !formData.password) {
      setError("กรุณากรอกชื่อผู้ใช้และรหัสผ่าน");
      setLoading(false);
      return;
    }

    try {
      const result = await login(formData);

      if (!result.success) {
        setError(result.message);
      }
    } catch (error) {
      setError("เกิดข้อผิดพลาดในการเข้าสู่ระบบ");
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "20px",
      }}
    >
      <div
        className="card"
        style={{
          width: "100%",
          maxWidth: "400px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🍦</div>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "700",
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              marginBottom: "8px",
            }}
          >
            GELATO SYSTEM
          </h1>
          <p style={{ color: "#64748b", fontSize: "16px" }}>
            Financial Comparison System
          </p>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: "24px" }}>
            <span>⚠️</span>
            <div>{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">ชื่อผู้ใช้</label>
            <input
              type="text"
              name="username"
              className="form-input"
              value={formData.username}
              onChange={handleChange}
              placeholder="กรอกชื่อผู้ใช้"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">รหัสผ่าน</label>
            <input
              type="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleChange}
              placeholder="กรอกรหัสผ่าน"
              disabled={loading}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg"
            style={{ width: "100%", marginTop: "24px" }}
            disabled={loading}
          >
            {loading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
          </button>
        </form>

        <div
          style={{
            marginTop: "24px",
            padding: "16px",
            background: "#f8fafc",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <small style={{ color: "#64748b" }}>
            <strong>ระบบยืนยันตัวตน:</strong>
            <br />
            กรุณาใช้ชื่อผู้ใช้และรหัสผ่านที่ได้รับจากผู้ดูแลระบบ
          </small>
        </div>

        <div
          style={{
            textAlign: "center",
            marginTop: "24px",
            fontSize: "12px",
            color: "#94a3b8",
          }}
        >
          GELATO SYSTEM v{process.env.REACT_APP_VERSION || "1.0.0"}
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const { user, isAuthenticated, isLoading, logout, hasPermission } = useAuth();

  // Menu items configuration with role-based access
  const menuItems = [
    { id: "dashboard", label: "แดชบอร์ด", icon: "🏠", permission: null },
    {
      id: "jobs",
      label: "จัดการงาน",
      icon: "💼",
      permission: "canManageProjects",
    },
    {
      id: "master-files",
      label: "Master Files",
      icon: "📁",
      permission: "canUploadFiles",
    },
    {
      id: "users",
      label: "จัดการผู้ใช้",
      icon: "👥",
      permission: "canManageUsers", // Only ผู้ดูแลระบบ can access
      roleRequired: "ผู้ดูแลระบบ", // Additional role check
    },
    {
      id: "logs",
      label: "บันทึกกิจกรรม",
      icon: "📋",
      permission: "canViewLogs",
    },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <Dashboard />;
      case "jobs":
        return <JobsManager />;
      case "master-files":
        return <MasterFiles />;
      case "users":
        return <Users />;
      case "logs":
        return <ActivityLogs />;
      default:
        return <Dashboard />;
    }
  };

  // Show loading spinner during auth check
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>กำลังโหลด...</p>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return (
    <div className="app-container">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <div className="logo">🍦 GELATO SYSTEM</div>
        </div>
        <nav className="nav-menu">
          {menuItems.map((item) => {
            // Check permissions
            if (item.permission && !hasPermission(item.permission)) {
              return null;
            }

            // Additional role check for sensitive menus
            if (item.roleRequired && user?.role !== item.roleRequired) {
              return null;
            }

            return (
              <div
                key={item.id}
                className={`nav-item ${activeTab === item.id ? "active" : ""}`}
                onClick={() => setActiveTab(item.id)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Header */}
      <header className={`header ${sidebarCollapsed ? "full-width" : ""}`}>
        <button
          className="menu-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? "☰" : "✕"}
        </button>
        <div className="header-content">
          <h1 className="header-title">
            {menuItems.find((item) => item.id === activeTab)?.label ||
              "GELATO SYSTEM"}
          </h1>
          <div className="user-info">
            <div className="user-details">
              <div className="user-name">
                {user?.full_name || user?.fullName || "ผู้ใช้ระบบ"}
              </div>
              <div className="user-role">
                {user?.department} - {user?.role}
              </div>
            </div>
            <div className="avatar">
              {(user?.full_name || user?.fullName || "User")
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </div>
            <button className="logout-btn" onClick={logout}>
              ออกจากระบบ
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`main-wrapper ${sidebarCollapsed ? "full-width" : ""}`}>
        <div className="main-content">
          <div className="content-wrapper">{renderContent()}</div>
        </div>
      </main>
    </div>
  );
}

// Wrap App with AuthProvider
function AppWithProvider() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWithProvider;
